import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Reveal } from './Reveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscribeSection = () => {
  const { t, language } = useLanguage();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    setErrorMessage('');
    try {
      await axios.post(`${API}/subscribe`, { email: email.trim(), language });
      setStatus('success');
      setEmail('');
    } catch (error) {
      setStatus('error');
      if (error.response?.status === 400) setErrorMessage(t.subscribe.error);
      else setErrorMessage(language === 'hu' ? 'Valami hiba történt. Próbáld újra.' : 'Something went wrong. Please try again.');
    }
  };

  return (
    <section
      id="subscribe"
      className="relative py-28 md:py-44 px-6 md:px-12 bg-background overflow-hidden"
      data-testid="subscribe-section"
    >
      <div className="max-w-[1480px] mx-auto">
        <div className="relative rounded-[32px] overflow-hidden border border-border/60 shadow-editorial">
          {/* Marble bg */}
          <div className="absolute inset-0 -z-0">
            <img
              src="https://images.pexels.com/photos/4705833/pexels-photo-4705833.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1400"
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-background/85 via-background/70 to-surface-highlight/70" />
            <div className="absolute inset-0 grain-layer opacity-50" />
          </div>

          <div className="relative px-6 md:px-16 py-20 md:py-28">
            <div className="max-w-3xl mx-auto text-center">
              <Reveal as="p" className="font-body text-[0.72rem] uppercase tracking-[0.32em] text-accent mb-6">
                {language === 'hu' ? '05 — Csatlakozz' : '05 — Join In'}
              </Reveal>

              <Reveal as="h2" duration={1.1} className="font-heading text-5xl md:text-6xl lg:text-7xl text-text-primary leading-[0.95] tracking-tight mb-6">
                <span className="block font-light">{t.subscribe.title.split(' ').slice(0, -1).join(' ') || t.subscribe.title}</span>
                <span className="block italic font-extralight text-text-secondary">
                  {t.subscribe.title.split(' ').slice(-1)[0]}
                  <span className="text-accent">.</span>
                </span>
              </Reveal>

              <Reveal as="p" delay={0.15} className="font-heading text-xl md:text-2xl italic text-text-secondary mb-6">
                {t.subscribe.subtitle}
              </Reveal>

              <Reveal as="p" delay={0.25} className="font-body text-base md:text-lg text-text-secondary leading-relaxed max-w-xl mx-auto mb-12">
                {t.subscribe.description}
              </Reveal>

              {status === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.16,1,0.3,1] }}
                  className="inline-flex items-center gap-3 px-6 py-4 bg-background/80 backdrop-blur-md border border-accent/40 rounded-full text-text-primary"
                  data-testid="subscribe-success"
                >
                  <CheckCircle2 size={20} className="text-accent" strokeWidth={1.5} />
                  <span className="font-body text-base">{t.subscribe.success}</span>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="max-w-xl mx-auto">
                  <Reveal delay={0.35} className="flex items-center gap-3 border-b border-text-primary/30 focus-within:border-accent transition-colors duration-500 pb-3">
                    <input
                      type="email"
                      placeholder={t.subscribe.placeholder}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1 bg-transparent border-0 outline-none focus:ring-0 font-body text-lg md:text-xl tracking-wide text-text-primary placeholder:text-text-secondary/60 py-3"
                      required
                      data-testid="subscribe-email-input"
                    />
                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="group flex items-center gap-2 font-body text-[0.78rem] uppercase tracking-[0.25em] text-text-primary hover:text-accent transition-colors duration-500 disabled:opacity-60"
                      data-testid="subscribe-submit-btn"
                    >
                      {status === 'loading' ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <>
                          {t.subscribe.cta}
                          <ArrowRight size={18} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform duration-500" />
                        </>
                      )}
                    </button>
                  </Reveal>

                  {status === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center justify-center gap-2 mt-5 text-error"
                    >
                      <AlertCircle size={16} strokeWidth={1.5} />
                      <span className="font-body text-sm">{errorMessage}</span>
                    </motion.div>
                  )}

                  <Reveal delay={0.45} className="mt-8 flex items-center justify-center gap-4">
                    <span className="block w-8 h-px bg-text-secondary/40" />
                    <span className="font-body text-[0.65rem] uppercase tracking-[0.3em] text-text-secondary">
                      {language === 'hu' ? 'Ingyenes PDF · Heti inspiráció' : 'Free PDFs · Weekly inspiration'}
                    </span>
                    <span className="block w-8 h-px bg-text-secondary/40" />
                  </Reveal>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SubscribeSection;
